import React from "react";
import { Box, Button, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import globals from "../../Globals";
import { replaceDeep } from "../../Layout/Router";
import { PageContainer, SectionHeader, SelectorBar, SelectField } from "../../Layout/Common";
import { gs, Loader, StaleHint, EventsTable, EVENT_KINDS } from "./servers";

// Game Servers → Logs: the lifecycle events of the title's machines, servers and sessions (`servers events`, newest
// first, 30 days kept), paged, filtered by kind and by a machine / server / session id. The first page refreshes every
// 10 s. Deep link: /<game>/servers/logs[/kind/<kind>][/machine/<id>][/server/<id>][/session/<id>].

const PAGE = 100;
const ANY = "*";
const ID_RE = /^[0-9a-f]{24}$/i;
const ID_FILTERS = [["machine", "Machine id"], ["server", "Server id"], ["session", "Session id"]];

// ["machine", "<id>", "kind", "server-crashed"] -> { machine, kind }
function filtersFromDeep(deep) {
    const f = { kind: ANY, machine: "", server: "", session: "" };
    // an id the backend would refuse (not 24 hex) is dropped from the link instead of failing every refresh
    for (let i = 0; i + 1 < (deep ?? []).length; i += 2) {
        const [k, v] = [deep[i], deep[i + 1]];
        if (k === "kind" || (k in f && ID_RE.test(v))) f[k] = v;
    }
    return f;
}

export default class LogsPage extends React.Component {
    state = { filters: filtersFromDeep(globals.route?.deep), skip: 0, events: null, more: false, loadedAt: null, stale: null, nonce: 0, idError: null };

    loader = new Loader(this, async () => {
        const { filters: f, skip, events } = this.state;
        if (skip > 0 && events && !this.force) return null;   // an older page stays still unless asked (new events would shift it)
        this.force = false;
        const payload = { skip, limit: PAGE };
        if (f.kind !== ANY) payload.kind = f.kind;
        if (f.machine) payload.machineId = f.machine;
        if (f.server) payload.serverId = f.server;
        if (f.session) payload.sessionId = f.session;
        const r = await gs("Loading events", ["events"], { payload, silent: true });
        return { events: r?.events ?? [], more: !!r?.more };
    }, 10000);

    componentDidMount() { this.loader.start(); }
    componentWillUnmount() { this.loader.stop(); }

    apply = (patch) => {
        const filters = { ...this.state.filters, ...patch };
        this.setState({ filters, skip: 0, events: null, idError: null }, this.loader.reload);
        replaceDeep(Object.entries(filters).filter(([, v]) => v && v !== ANY).flat());
    };
    // An id filter applies on Enter or blur; the backend ignores an id it cannot parse, so a malformed one is refused here
    setId = (key, value) => {
        const v = value.trim();
        if (v === this.state.filters[key]) return;
        if (v && !ID_RE.test(v)) { this.setState({ idError: `${key} id must be 24 hex characters` }); return; }
        this.apply({ [key]: v });
    };
    page = (skip) => this.setState({ skip: Math.max(0, skip), events: null }, this.loader.reload);
    clear = () => { this.setState(st => ({ nonce: st.nonce + 1 })); this.apply({ kind: ANY, machine: "", server: "", session: "" }); };

    render() {
        const { filters, skip, events, more, loadedAt, stale, nonce, idError } = this.state;
        const active = filters.kind !== ANY || filters.machine || filters.server || filters.session;
        return (
            <PageContainer maxWidth={1300}>
                <SectionHeader title="Events">
                    <StaleHint loadedAt={loadedAt} stale={stale} />
                    <Tooltip title="Refresh"><IconButton size="small" onClick={() => { this.force = true; this.loader.reload(); }}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </SectionHeader>
                <SelectorBar sx={{ flexWrap: "wrap" }}>
                    <SelectField label="Kind" size="small" minWidth={220} value={filters.kind} onChange={v => this.apply({ kind: v })}
                                 options={[{ value: ANY, label: "Any kind" }, ...EVENT_KINDS]} />
                    {ID_FILTERS.map(([key, label]) => (
                        <TextField key={`${key}-${nonce}`} size="small" label={label} sx={{ width: 240 }} defaultValue={filters[key]}
                                   slotProps={{ htmlInput: { style: { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 13 } } }}
                                   onKeyDown={e => { if (e.key === "Enter") this.setId(key, e.target.value); }}
                                   onBlur={e => this.setId(key, e.target.value)} />
                    ))}
                    <Button size="small" onClick={this.clear} disabled={!active}>Clear</Button>
                </SelectorBar>
                {idError && <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>{idError}</Typography>}
                {events == null
                    ? <Typography variant="body2" color="text.secondary">{stale ? `Could not load the events: ${stale}` : "Loading…"}</Typography>
                    : <EventsTable events={events} />}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                    <Button size="small" disabled={skip === 0} onClick={() => this.page(skip - PAGE)}>← Newer</Button>
                    <Typography variant="caption" color="text.secondary">
                        {events && events.length > 0 ? `events ${skip + 1}–${skip + events.length}, newest first` : ""}{skip > 0 ? " · not refreshed on older pages" : ""}
                    </Typography>
                    <Button size="small" disabled={!more} onClick={() => this.page(skip + PAGE)}>Older →</Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    Events are kept 30 days. A machine id opens the machine, a server id opens its machine with that server's log, a session id opens the session.
                </Typography>
            </PageContainer>
        );
    }
}
